import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';
import { showRealTimeNotification, showTripNotification } from '../utils/notifications';

const RealTimeNotifications = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    // Subscribe to trip_requests changes
    const tripSubscription = supabase
      .channel('trip-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trip_requests'
        },
        (payload) => {
          handleTripUpdate(payload);
        }
      )
      .subscribe();

    // Subscribe to participants changes
    const participantSubscription = supabase
      .channel('participant-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'participants'
        },
        (payload) => {
          handleParticipantUpdate(payload);
        }
      )
      .subscribe();

    return () => {
      tripSubscription.unsubscribe();
      participantSubscription.unsubscribe();
    };
  }, [user]);

  const handleTripUpdate = (payload) => {
    try {
      const { eventType, new: newRecord, old: oldRecord } = payload;
      
      if (!newRecord) return;

      const tripDetails = {
        pickup_location: newRecord.pickup_location,
        dropoff_location: newRecord.dropoff_location,
        id: newRecord.id
      };

      // Handle status changes
      if (oldRecord && oldRecord.status !== newRecord.status) {
        showRealTimeNotification.tripStatusChanged(
          oldRecord.status,
          newRecord.status,
          tripDetails
        );
      }

      // Handle seat count changes
      if (oldRecord && oldRecord.seats_filled !== newRecord.seats_filled) {
        const oldCount = oldRecord.seats_filled || 0;
        const newCount = newRecord.seats_filled || 0;
        
        showTripNotification.seatCountUpdated(oldCount, newCount, tripDetails);

        // Check if minimum is met
        const minSeats = newRecord.min_seat_count || 2;
        if (newCount >= minSeats && oldCount < minSeats) {
          showTripNotification.minimumMet(tripDetails);
        } else if (newCount < minSeats && oldCount >= minSeats) {
          showTripNotification.minimumNotMet(newCount, minSeats, tripDetails);
        }
      }

      // Handle new trip creation
      if (eventType === 'INSERT') {
        showTripNotification.created(tripDetails);
      }

      // Handle trip cancellation
      if (newRecord.status === 'cancelled' && oldRecord?.status !== 'cancelled') {
        showTripNotification.cancelled(tripDetails);
      }
    } catch (error) {
      console.error('❌ Error handling trip update:', error);
    }
  };

  const handleParticipantUpdate = (payload) => {
    try {
      const { eventType, new: newRecord, old: oldRecord } = payload;

      // Get trip details for the participant
      const getTripDetails = async (tripRequestId) => {
        try {
          const { data: trip, error } = await supabase
            .from('trip_requests_public')
            .select('pickup_location, dropoff_location')
            .eq('id', tripRequestId)
            .single();

          if (error) {
            console.error('❌ Error fetching trip details:', error);
            return null;
          }

          return trip;
        } catch (error) {
          console.error('❌ Error in getTripDetails:', error);
          return null;
        }
      };

      // Handle participant joining
      if (eventType === 'INSERT' && newRecord) {
        getTripDetails(newRecord.trip_request_id).then(tripDetails => {
          if (tripDetails) {
            showRealTimeNotification.participantJoined(
              newRecord.user_name || 'Someone',
              tripDetails
            );
          }
        }).catch(error => {
          console.error('❌ Error handling participant join:', error);
        });
      }

      // Handle participant leaving
      if (eventType === 'DELETE' && oldRecord) {
        getTripDetails(oldRecord.trip_request_id).then(tripDetails => {
          if (tripDetails) {
            showRealTimeNotification.participantLeft(
              oldRecord.user_name || 'Someone',
              tripDetails
            );
          }
        }).catch(error => {
          console.error('❌ Error handling participant leave:', error);
        });
      }
    } catch (error) {
      console.error('❌ Error handling participant update:', error);
    }
  };

  // This component doesn't render anything visible
  return <div data-testid="real-time-notifications" style={{ display: 'none' }} />;
};

export default RealTimeNotifications;
